<template>
  <div ref="table" class="trade-last-orders-table">
    <div
      :class="{
        [`trade-last-orders-table--${tradeType}`]: true,
        'mt-auto': tradeType === TRADE_TYPE.SELL,
      }"
      class="trade-last-orders-table__body"
    >
      <div
        v-for="(row, index) in rows"
        :key="index"
        v-tooltip="tooltipConfig"
        :class="row.rowClass"
        :style="`--percent: ${row.percentage}%`"
        class="trade-last-orders-table__row txt txt--color-03 txt--12px pl-12 pr-12"
        @click="handleRowClick(row)"
        @mouseover="rowHoverHandler(row)"
        @mouseout="rowUnhoverHandler"
      >
        <div
          v-for="(_, indKey) in heading"
          :key="indKey"
          :data-label="compColumnValue(heading[indKey])"
          :class="[
            row[indKey].class,
            indKey === 'price' ? 'txt--left' : 'txt--right',
          ]"
          class="trade-last-orders-table__cell"
        >
          <slot :name="'td-' + indKey" v-bind="row">
            {{ compColumnValue(row[indKey]) }}
            <!-- {{ $filters.formatNumber($filters.shortifyNumber(row[ indKey ])) }} -->
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import { AlignType } from '@/lib/toolTipAlignmentType'
import { mapFieldsToComputed } from '@/store/lib/vuex-form'
import { Decimal } from 'decimal.js-light'
import { calculateTotalWithFee } from '@/store/buySell'
import { BUY_SELL_FORMS, TRADE_TYPE, MARGIN_TRADE_CROSS } from '@/lib/constants'
import { wait } from '@/lib/helpers'
import { valFromObj, getSumFromStack } from '@/lib/utils'

// const noExponent = (val) => {
//   var data = String(val).split(/[eE]/)
//   if (data.length == 1) return data[0]

//   let z = '',
//     sign = val < 0 ? '-' : ''
//   let str = data[0].replace('.', '')
//   let mag = Number(data[1]) + 1

//   if (mag < 0) {
//     z = sign + '0.'
//     while (mag++) z += '0'
//     return z + str.replace(/^-/, '')
//   }
//   mag -= str.length
//   while (mag--) z += '0'
//   return str + z
// }

// const valShorten = (curVal) => {
//     const numberVal = Number(curVal)
//     if (numberVal > 999) {
//         return noExponent(parseFloat(numberVal.toFixed(2)))
//     }

//     if (numberVal > 99) {
//         return noExponent(parseFloat(numberVal.toFixed(3)))
//     }

//     if (numberVal >= 0.01) {
//         return noExponent(parseFloat(numberVal.toFixed(5)))
//     }

//     if (numberVal < 0.01) {
//         return noExponent(parseFloat(numberVal.toFixed(8)))
//     }

//     return noExponent(numberVal)
// }

export default {
  name: 'TradeLastOrdersTable',
  props: {
    heading: {
      type: Object,
      required: true,
    },
    rowsData: {
      type: Array,
      required: true,
    },
    tradeType: {
      type: String,
      required: true,
      validator: value => [TRADE_TYPE.SELL, TRADE_TYPE.BUY].includes(value),
    },
    scrollToBottom: {
      type: Boolean,
      default: false,
    },
    currentType: {
      validator: v => ['all', 'sell', 'buy'].includes(v),
      required: true,
    },
  },
  data () {
    return {
      hoveredRowIndex: -1,
      tooltipConfig: {
        alignment: {
          target: {
            horizontal: AlignType.Middle,
            vertical: AlignType.Top,
          },
          overlay: {
            horizontal: AlignType.Middle,
            vertical: AlignType.Top,
          },
        },
      },

      TRADE_TYPE,
    }
  },
  computed: {
    ...mapGetters({
      amountCurrency: 'pairs/amountCurrency',
      totalCurrency: 'pairs/totalCurrency',
      getCurrentPair: 'pairs/getCurrentPair',
    }),
    ...mapGetters({
      getMarginBalanceByTicker: 'wallet/getMarginBalanceByTicker',
    }),
    ...mapGetters({
      getCurrencyAvailableBalance: 'wallet/getCurrencyAvailableBalance',
    }),
    ...mapState({
      pair: state => state.pairs.currentPair || state.globals.defaultPair,
      fee: state => state.buySell.fee,
    }),
    ...mapFieldsToComputed('exchange/exchangeMarketSellForm', {
      giveSell: 'give',
      // receiveSell: "receive",
      // giveCurrencySell: "giveCurrency",
      // receiveCurrencySell: "receiveCurrency"
    }),
    ...mapFieldsToComputed('exchange/exchangeMarketBuyForm', {
      // giveBuy: "give",
      receiveBuy: 'receive',
      // giveCurrencyBuy: "giveCurrency",
      // receiveCurrencyBuy: "receiveCurrency"
    }),
    isMarginTrade () {
      return this.$store.state.trade.currentTradeType === MARGIN_TRADE_CROSS
    },
    rows: {
      get () {
        const q = 20 // quantity of rows
        const rows = [...this.rowsData]
        const preparedRows = (() => {
          if (this.currentType === 'all') {
            // for buy get from beginning
            if (this.tradeType === TRADE_TYPE.BUY) return rows.slice(0, q)
            // for sell get from ending
            if (this.tradeType === TRADE_TYPE.SELL) return rows.reverse().slice(0, q).reverse()
          }
          return rows
        })()
        return preparedRows.map(([price, amount, total]) => ({
          price: {
            value: price,
            class: this.tradeType === TRADE_TYPE.SELL ? 'txt--red' : 'txt--green',
          },
          amount,
          total,
          percentage: ((amount / this.totalAmount) * 100).toFixed(2),
          color: this.tradeType === TRADE_TYPE.SELL ? 'txt--red' : 'txt--green',
        }))
      },
      set (v) {
        return v
      },
    },
    stockTicker () { // main ticker from pair ex. ETH (ETH/BTC)
      return this.getCurrentPair.stockTicker
    },
    moneyTicker () { // second ticker from pair ex. BTC (ETH/BTC)
      return this.getCurrentPair.moneyTicker
    },
    stockBalance () {
      if (this.isMarginTrade) {
        const valDecimal = this.getMarginBalanceByTicker(
          this.stockTicker,
        ).marginBalance
        return valDecimal
      }
      return this.getCurrencyAvailableBalance(this.stockTicker)
    },
    moneyBalance () {
      if (this.isMarginTrade) {
        const valDecimal = this.getMarginBalanceByTicker(
          this.moneyTicker,
        ).marginBalance
        return valDecimal
      }
      return this.getCurrencyAvailableBalance(this.moneyTicker)
    },
    tooltipContent: {
      get () {
        if (this.hoveredRowIndex > -1) {
          return this.getTooltipInfo()
        }

        return undefined
      },
      set (v) {
        return v
      },
    },
    totalAmount () {
      return this.rowsData.reduce((sum, current) => sum + Number(current[1]), 0)
    },
  },
  watch: {
    scrollToBottom: {
      async handler (v) {
        await wait()
        this.scrollToInitialPosition(v)
      },
      immediate: true,
    },
    currentType (newVal) {
      if (newVal === 'sell' && process.client) {
        setTimeout(() => {
          this.$refs.table.scrollTop = this.$refs.table.scrollHeight
        })
      }
    },
    rows: {
      async handler (newRows = [], oldRows = []) {
        if (this.hoveredRowIndex > -1) {
          this.assignHoveredRowIndex(newRows, oldRows, 'sell')
          this.assignHoveredRowIndex(newRows, oldRows, 'buy')
          this.setTooltipContent()
        }

        if (!oldRows.length && newRows.length && this.scrollToBottom) {
          await wait()
          this.scrollToInitialPosition(this.scrollToBottom)
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    ...mapMutations({
      updateClearErrorsToWatch: 'buySell/updateClearErrorsToWatch',
    }),
    valShorten (val) {
      return this.$filters.shortifyNumber(val)
    },
    // ...mapActions({
    //     changeBuyField: "buySell/buyForm/changeField", // TODO: buySellRefactor
    //     changeSellField: "buySell/sellForm/changeField", // TODO: buySellRefactor

    // }),
    scrollToInitialPosition (v) {
      const elem = this.$refs.table
      if (!elem) return
      elem.scrollTop = v ? elem.scrollHeight : 0
    },
    assignHoveredRowIndex (newRows, oldRows, tradeType) {
      const diff = newRows.length - oldRows.length
      if (this.tradeType === tradeType && newRows.length !== oldRows.length) {
        this.hoveredRowIndex =
          this.hoveredRowIndex <= newRows.length - 1
            ? this.hoveredRowIndex
            : this.hoveredRowIndex + diff
      }
    },
    compColumnValue (col) {
      if (col instanceof Object) return col.value
      return col
    },
    getRows () {
      return this.tradeType === TRADE_TYPE.BUY ? this.rows : [...this.rows].reverse()
    },
    handleRowClick (row) {
      const rows = this.getRows()
      const { price } = row
      const amount = getSumFromStack({
        rows,
        row,
        columnFn: row => row.amount,
      })

      // this.changeBuyField({
      //     field: "price",
      //     value: price.toString(),
      //     noInterceptors: true,
      // });
      // this.changeSellField({
      //     field: "price",
      //     value: price.toString(),
      //     noInterceptors: true,
      // });

      // this.assignMarketAmount(amount)

      const total = getSumFromStack({
        rows,
        row,
        columnFn: row => row.total,
      })

      // const totalWithFee = new Decimal(calculateTotalWithFee('buy', calculateTotal(amount, price), this.fee));
      const totalWithFee = new Decimal(
        calculateTotalWithFee({
          tradeType: TRADE_TYPE.BUY,
          total,
          fee: this.fee,
        }),
      )

      // if(this.tradeType === 'sell') {
      //     if (totalWithFee.greaterThan(this.moneyBalance)) {
      //         this.changeBuyField({
      //             field: "totalWithFee",
      //             value: this.moneyBalance.toString(),
      //         });
      //     } else {
      //         this.changeBuyField({
      //             field: "amount",
      //             value: amount.toString(),
      //         });
      //     }

      //     // this.changeSellField({
      //     //     field: "amount",
      //     //     value: '',
      //     // });
      // }

      // if(this.tradeType === 'buy') {
      //     this.changeSellField({
      //         field: "amount",
      //         value: amount.lessThan(this.stockBalance)
      //             ? amount.toString()
      //             : this.stockBalance.toString(),
      //     });

      //     // this.changeBuyField({
      //     //     field: "amount",
      //     //     value: '',
      //     // });
      // }

      // FOR NEW buySell FIELDS ///////////////////////////////////////////

      const resetOtherFields = (type) => {
        BUY_SELL_FORMS.forEach((formName) => {
          ;['buySell/setBuySellFormAmount', 'buySell/setBuySellFormTotal'].map(
            mutation =>
              this.$store.commit(mutation, {
                formName,
                type,
                value: '',
                currentPair: this.pair,
              }),
          )
        })
      }

      BUY_SELL_FORMS.forEach((formName) => {
        ;['buy', 'sell'].map(type =>
          this.$store.commit('buySell/setBuySellFormPrice', {
            formName,
            type,
            value: valFromObj(price).toString(),
            currentPair: this.pair,
          }),
        )
      })

      // TODO: maybe this is better (merge conflict)?
      // ['buy', 'sell'].map(type => this.$store.commit('buySell/setBuySellFormPrice', {
      //     type,
      //     value: price.value.toString(), // valFromObj(price).toString(),
      //     currentPair: this.pair
      // }))

      if (this.tradeType === TRADE_TYPE.BUY) {
        BUY_SELL_FORMS.forEach((formName) => {
          this.$store.commit('buySell/setBuySellFormAmount', {
            formName,
            type: 'sell',
            value: amount.lessThan(this.stockBalance)
              ? amount.toString()
              : this.stockBalance.toString(),
            currentPair: this.pair,
          })
        })

        // reset fields on other form
        resetOtherFields('buy')
      }

      if (this.tradeType === TRADE_TYPE.SELL) {
        if (totalWithFee.greaterThan(this.moneyBalance)) {
          BUY_SELL_FORMS.forEach((formName) => {
            this.$store.commit('buySell/setBuySellFormTotal', {
              formName,
              type: 'buy',
              value: this.moneyBalance.toString(),
              currentPair: this.pair,
            })
          })
          // this.changeBuyField({
          //     field: "totalWithFee",
          //     value: this.moneyBalance.toString(),
          // });
        } else {
          BUY_SELL_FORMS.forEach((formName) => {
            this.$store.commit('buySell/setBuySellFormAmount', {
              formName,
              type: 'buy',
              value: amount.toString(),
              currentPair: this.pair,
            })
          })
          // this.changeBuyField({
          //     field: "amount",
          //     value: amount.toString(),
          // });
        }

        // reset fields on other form
        resetOtherFields('sell')
      }

      // after setting new form values, if on one of trade forms was errors - clear them
      this.updateClearErrorsToWatch()
    },
    assignMarketAmount (amount) {
      if (this.tradeType === TRADE_TYPE.BUY) {
        this.giveSell = amount
      } else {
        this.receiveBuy = amount
      }
    },
    rowHoverHandler (row) {
      const rowIndex = this.rows.indexOf(row)

      this.hoveredRowIndex = rowIndex

      this.setTooltipContent()
    },
    rowUnhoverHandler () {
      this.hoveredRowIndex = -1
      this.tooltipContent = undefined
      this.setTooltipContent()
    },
    setTooltipContent () {
      this.rows = this.rows.map((row, index) => {
        if (
          this.hoveredRowIndex > -1 &&
          this.tradeType === TRADE_TYPE.BUY &&
          index <= this.hoveredRowIndex
        ) {
          return (row.rowClass = 'hovered')
        }
        if (
          this.hoveredRowIndex > -1 &&
          this.tradeType === TRADE_TYPE.SELL &&
          index >= this.hoveredRowIndex
        ) {
          return (row.rowClass = 'hovered')
        }
        return (row.rowClass = '')
      })

      this.tooltipConfig = {
        ...this.tooltipConfig,
        content: this.tooltipContent,
      }
    },
    getTooltipInfo () {
      const row = this.rows[this.hoveredRowIndex]
      const rows = this.getRows()
      // const { amount, total, price } = row

      const rowIndex =
        this.tradeType === TRADE_TYPE.BUY
          ? this.hoveredRowIndex
          : this.rows.length - this.hoveredRowIndex - 1

      const totalPrice = getSumFromStack({
        rows,
        row,
        columnFn: row => row.price.value,
      })

      const avaragePrice = totalPrice / (rowIndex + 1)

      const amountSum = getSumFromStack({
        rows,
        row,
        columnFn: row => row.amount,
      })

      const totalSum = getSumFromStack({
        rows,
        row,
        columnFn: row => row.total,
      })

      const prepareNum = (num) => {
        if (num.lessThan(1)) {
          const res = num.toDecimalPlaces(8, Decimal.ROUND_UP).toFixed(8)

          return res
        }

        return num
      }

      return `Avg Price: ${parseFloat(avaragePrice.toFixed(8))} <br>
                    Amount ${this.amountCurrency}: ${prepareNum(amountSum)} <br>
                    Total ${this.totalCurrency}: ${prepareNum(totalSum)}`
    },
  },
}
</script>

<style lang="scss" scoped>
.trade-last-orders-table {
  $root: &;
  $avarage-block-height: 29px;

  display: flex;
  flex-direction: column;

  max-height: calc(100% - #{$avarage-block-height});
  overflow-y: auto;
  overflow-x: hidden;

  @include bp(l) {
    max-height: 100%;
  }

  &--buy {
    #{$root}__row:hover {
      @include bp(m) {
        border-bottom: 1px dashed color(main);
      }
    }
  }

  &--sell {
    #{$root}__row:hover {
      @include bp(m) {
        border-top: 1px dashed color(main);
      }
    }
  }

  &__row {
    display: flex;
    align-items: center;
    cursor: pointer;
    border-top: 1px solid transparent;
    border-bottom: 1px solid transparent;
    position: relative;
    // border-bottom: 1px solid map-get($borderColors, 'transp-01');

    // &:nth-child(odd) {
    //   background-color: rgba(grey, 0.07);
    //   // background-color: rgba(map-get($backgroundColors, 'theme-03'), .5);
    // }

    // &:nth-child(even) {
    //     background-color: map-get($backgroundColors, 'theme-02');
    // }

    &::before {
      height: 90%;
      position: absolute;
      top: 0;
      left: 0;
      width: var(--percent);
      background: color(red-transp);
      content: '';
      transition: width .3s ease;

      #{$root}--buy & {
        background: color(green-transp);
      }

      #{$root}--sell & {
        background: color(red-transp);
      }
    }
  }

  &__row &__cell:nth-child(2) {
    display: none;

    @include bp(m) {
      display: block;
    }
  }

  &__cell {
    flex: 1 1 0;
    padding: toRem(5px) toRem(2px);
    position: relative;
    z-index: 2;
    @include bp(m) {
      padding: toRem(3px) toRem(2px);
    }
  }

  &__row.hovered {
    background-color: color(grey-transp);
  }
}
</style>
